import type { DeviceSpecDevice, DeviceSpecTag, EdgeDevice, EdgeDeviceChannel, EdgeTag } from '../../types'
import { channelDef } from './catalog'
import { addressKey, channelFromEdge, pairTags } from './pullMerge'

// 명세 ↔ Edge 비교 — 두 방향(Edge → 명세 가져오기 · 명세 → Edge 적용)이 같은 짝짓기·같은 비교를 쓴다.
//  · 태그는 주소로 짝짓는다 (대소문자·띄어쓰기·숫자 앞 0 무시 — D02000 = d2000). 표기만 다른 주소는 같은 값으로 본다
//  · 비교는 방향 없이 한 번 하고(compareDevice), 방향마다 "실행하면 바뀌는 것"으로 풀어낸다(tagChanges · summarize)
//  · 화면은 늘 명세 값을 보여 주고 Edge 값은 말풍선으로 — 기준은 명세

export type Direction = 'pull' | 'push' // pull = Edge → 명세 가져오기, push = 명세 → Edge 적용
export type TagField = 'address' | 'size' | 'dataType' | 'arithmetic' | 'alias'

// Edge 태그 값을 명세 칸 모양(문자열)으로
export function edgeTagValues(e: EdgeTag): Record<TagField, string> {
  return {
    address: e.address ?? '',
    size: String(e.size),
    dataType: e.dataType,
    arithmetic: e.arithmetic ?? '',
    alias: e.alias?.trim() ?? '',
  }
}

export interface CompareRow {
  key: string
  spec?: DeviceSpecTag // 없으면 Edge 에만 있는 태그
  edge?: EdgeTag // 없으면 명세에만 있는 태그
  diffs: TagField[] // 둘 다 있을 때 값이 다른 칸
  edgeAliasEmpty: boolean // 명세에는 Alias 가 있는데 Edge 에는 비어 있음
}

export interface DeviceField {
  label: string
  spec: string
  edge: string
  secret?: boolean
}

export interface DeviceCompare {
  fields: DeviceField[] // 값이 다른 디바이스 칸 (이름·설명·채널·채널 설정)
  rows: CompareRow[] // 명세 순서, Edge 에만 있는 태그는 Edge 에서 바로 앞 태그 뒤에
  channelLoaded: boolean // 채널 설정까지 견줬는지
}

// spec·edge 중 하나가 없으면 다른 쪽만으로 행을 만든다 (새로 추가되는 디바이스)
export function compareDevice(spec: DeviceSpecDevice | undefined, edge: EdgeDevice | undefined, channel: EdgeDeviceChannel | undefined, channelType: string): DeviceCompare {
  const specTags = spec?.tags ?? []
  const edgeTags = edge?.tags ?? []
  const olds = pairTags(channelType, edgeTags, specTags)
  const edgeOf = new Map<string, EdgeTag>()
  olds.forEach((o, i) => { if (o) edgeOf.set(o.key, edgeTags[i]) })

  const rows: CompareRow[] = specTags.map(t => {
    const e = edgeOf.get(t.key)
    if (!e) return { key: t.key, spec: t, diffs: [], edgeAliasEmpty: false }
    const ev = edgeTagValues(e)
    const diffs = (Object.keys(ev) as TagField[]).filter(f => f === 'address'
      ? addressKey(channelType, t.address) !== addressKey(channelType, ev.address)
      : (t[f] ?? '').trim() !== ev[f].trim())
    return { key: t.key, spec: t, edge: e, diffs, edgeAliasEmpty: !!t.alias?.trim() && !ev.alias }
  })
  // Edge 에만 있는 태그 — Edge 에서 바로 앞에 있던 짝지어진 태그 뒤에 (앞에 없으면 맨 앞)
  let anchor: string | null = null
  const after = new Map<string | null, CompareRow[]>()
  edgeTags.forEach((e, i) => {
    const old = olds[i]
    if (old) { anchor = old.key; return }
    after.set(anchor, [...(after.get(anchor) ?? []), { key: `edge:${e.id}`, edge: e, diffs: [], edgeAliasEmpty: false }])
  })
  const ordered = [...(after.get(null) ?? [])]
  for (const r of rows) ordered.push(r, ...(after.get(r.key) ?? []))

  return { fields: spec && edge ? deviceFields(spec, edge, channel) : [], rows: ordered, channelLoaded: !!channel }
}

function deviceFields(spec: DeviceSpecDevice, edge: EdgeDevice, channel: EdgeDeviceChannel | undefined): DeviceField[] {
  const out: DeviceField[] = []
  const field = (label: string, s: string, e: string, secret?: boolean) => {
    if ((s ?? '').trim() !== (e ?? '').trim()) out.push({ label, spec: s ?? '', edge: e ?? '', secret })
  }
  field('디바이스 이름', spec.name, edge.name)
  field('설명', spec.description, edge.description ?? '')
  const edgeType = channel?.channelType ?? edge.channelType
  field('프로토콜', channelDef(spec.channelType)?.label ?? spec.channelType, channelDef(edgeType)?.label ?? edgeType)
  // 프로토콜이 같을 때만 칸을 견준다 — 다르면 채널 설정이 통째로 바뀐다
  if (channel && channel.channelType === spec.channelType) {
    const def = channelDef(channel.channelType)
    const edgeValues = channelFromEdge(channel.channelInfoJson)
    const keys = [...new Set([...(def?.fields.map(f => f.key) ?? []), ...Object.keys(edgeValues)])]
    for (const k of keys) {
      const f = def?.fields.find(x => x.key === k)
      const secret = !!f?.secret || k.toLowerCase() === 'password'
      // Edge 는 비밀번호를 비워 보낸다 — 견줄 수 없으니 빼고 본다
      if (secret && !edgeValues[k]) continue
      field(f?.label ?? k, spec.channel[k] ?? '', edgeValues[k] ?? '', secret)
    }
  }
  return out
}

// ── 방향별 해석 ──

export type RowAction = 'same' | 'change' | 'add' | 'remove' | 'keep'

// 실행하면 이 행이 어떻게 되는지 + 바뀌는 칸
export function rowAction(r: CompareRow, dir: Direction, keepSpecOnly: boolean): { action: RowAction; changed: TagField[] } {
  if (r.spec && r.edge) {
    // 가져오기는 Edge Alias 가 비어 있으면 명세 값을 남긴다 (구버전 Edge 는 Alias 를 보내지 않는다)
    const changed = dir === 'pull' ? r.diffs.filter(f => !(f === 'alias' && r.edgeAliasEmpty)) : r.diffs
    return { action: changed.length > 0 ? 'change' : 'same', changed }
  }
  if (r.spec) return { action: dir === 'push' ? 'add' : keepSpecOnly ? 'keep' : 'remove', changed: [] }
  return { action: dir === 'pull' ? 'add' : 'remove', changed: [] }
}

export interface CompareSummary {
  device: number // 바뀌는 디바이스 칸
  changed: number // 바뀌는 태그
  add: number
  remove: number
  aliasEmpty: number // Edge Alias 비어 있음
}

export function summarize(c: DeviceCompare, dir: Direction, keepSpecOnly: boolean): CompareSummary {
  const s: CompareSummary = { device: c.fields.length, changed: 0, add: 0, remove: 0, aliasEmpty: 0 }
  for (const r of c.rows) {
    const { action } = rowAction(r, dir, keepSpecOnly)
    if (action === 'change') s.changed++
    else if (action === 'add') s.add++
    else if (action === 'remove') s.remove++
    if (r.edgeAliasEmpty) s.aliasEmpty++
  }
  return s
}

export type PlanState =
  | { kind: 'create' }
  | { kind: 'pending' }
  | { kind: 'same'; summary: CompareSummary }
  | { kind: 'update'; summary: CompareSummary }

// channelFailed: 채널 설정을 못 받았으면 접속 정보는 빼고 견준다
export function planOf(spec: DeviceSpecDevice | undefined, edge: EdgeDevice | undefined, channel: EdgeDeviceChannel | undefined,
  dir: Direction, keepSpecOnly: boolean, channelFailed = false): PlanState {
  if (dir === 'pull' ? !spec : !edge) return { kind: 'create' }
  if (!channel && !channelFailed) return { kind: 'pending' }
  const summary = summarize(compareDevice(spec, edge, channel, compareType(spec, edge, channel, dir)), dir, keepSpecOnly)
  return summary.device + summary.changed + summary.add + summary.remove > 0 ? { kind: 'update', summary } : { kind: 'same', summary }
}

// 주소 짝짓기에 쓸 프로토콜 — 실행 뒤에 남을 쪽 (가져오기면 Edge, 적용이면 명세)
export function compareType(spec: DeviceSpecDevice | undefined, edge: EdgeDevice | undefined, channel: EdgeDeviceChannel | undefined, dir: Direction): string {
  const edgeType = channel?.channelType ?? edge?.channelType ?? ''
  return dir === 'pull' ? edgeType || spec?.channelType || '' : spec?.channelType || edgeType
}
